<template>
  <div class="file-upload-field">
    <div
      class="d-flex align-center justify-space-between mb-1"
      v-if="label || (multiple && maxFiles)"
    >
      <span class="caption font-weight-medium" v-if="label">
        {{ label }} <span class="error--text" v-if="required">*</span>
      </span>
      <span v-if="multiple && maxFiles" class="caption text--secondary">
        {{ items.length }}/{{ maxFiles }}
      </span>
    </div>

    <input
      ref="input"
      type="file"
      class="d-none"
      :accept="computedAccept"
      :multiple="multiple"
      :disabled="disabled"
      @change="onFilesSelected"
    />

    <!-- Compact circular / avatar style picker (best for a single profile-like image) -->
    <div v-if="variant === 'avatar'" class="d-flex align-center">
      <v-avatar
        :size="avatarSize"
        class="file-upload-avatar"
        :class="{
          'file-upload-avatar--empty': !items.length,
          'file-upload-avatar--disabled': disabled,
        }"
        @click.native="openPicker"
      >
        <v-img v-if="items.length && items[0].url" :src="items[0].url" :aspect-ratio="1">
          <div
            v-if="items[0].uploading"
            class="file-upload-overlay d-flex align-center justify-center"
          >
            <v-progress-circular indeterminate size="22" width="2" color="white" />
          </div>
          <div
            v-if="items[0].error"
            class="file-upload-overlay file-upload-overlay--error d-flex align-center justify-center"
          >
            <v-icon color="white" small>mdi-alert-circle-outline</v-icon>
          </div>
        </v-img>
        <v-icon v-else :size="Math.round(avatarSize * 0.45)" color="grey darken-1">{{
          icon
        }}</v-icon>

        <div v-if="!disabled" class="file-upload-avatar-edit">
          <v-icon size="13" color="white">mdi-camera</v-icon>
        </div>
      </v-avatar>

      <div class="ml-3 d-flex flex-column">
        <span v-if="hintText" class="caption text--secondary">{{ hintText }}</span>
        <v-btn
          v-if="items.length && deletable && !disabled"
          text
          x-small
          color="error"
          class="pl-0 justify-start"
          @click="removeItem(0)"
        >
          <v-icon x-small left>mdi-delete-outline</v-icon>
          {{ $t("remove_file") }}
        </v-btn>
      </div>
    </div>

    <!-- Dropzone style picker (supports single or multiple files, drag & drop) -->
    <template v-else>
      <v-card
        outlined
        rounded="lg"
        class="file-upload-zone d-flex flex-column align-center justify-center text-center"
        :class="{
          'file-upload-zone--dragging': dragging,
          'file-upload-zone--error': hasError,
          'file-upload-zone--disabled': disabled || atMaxFiles,
          'py-8 px-4': !compact,
          'py-4 px-3': compact,
        }"
        ripple
        @click="openPicker"
        @dragover.prevent="onDragOver"
        @dragleave.prevent="dragging = false"
        @drop.prevent="onDrop"
      >
        <v-icon :size="compact ? 26 : 42" color="primary" class="mb-2">{{ icon }}</v-icon>
        <div class="body-2 font-weight-medium grey--text text--darken-2">
          {{ dropText }}
          <span class="primary--text">{{ $t("click_to_browse") }}</span>
        </div>
        <div class="caption text--secondary mt-1">{{ hintText }}</div>
      </v-card>

      <v-row v-if="items.length" dense class="mt-3">
        <v-col v-for="(item, index) in items" :key="item.uid" :cols="previewCols">
          <v-hover v-slot="{ hover }">
            <v-card
              outlined
              rounded="lg"
              class="file-upload-preview"
              :class="{ 'file-upload-preview--error': item.error }"
            >
              <v-img
                v-if="isImageItem(item)"
                :src="item.url"
                aspect-ratio="1"
                class="grey lighten-4"
              >
                <v-chip
                  v-if="index === 0 && multiple && !item.uploading"
                  x-small
                  color="primary"
                  class="file-upload-badge"
                >
                  {{ $t("main_file") }}
                </v-chip>

                <div
                  v-if="item.uploading"
                  class="file-upload-overlay d-flex flex-column align-center justify-center"
                >
                  <v-progress-circular
                    :value="item.progress"
                    size="30"
                    width="3"
                    color="primary"
                  />
                  <span class="caption white--text mt-1">{{ item.progress }}%</span>
                </div>

                <div
                  v-if="item.error"
                  class="file-upload-overlay file-upload-overlay--error d-flex flex-column align-center justify-center"
                >
                  <v-icon color="white">mdi-alert-circle-outline</v-icon>
                  <span class="caption white--text mt-1">{{ $t("upload_failed") }}</span>
                  <v-btn x-small text color="white" @click.stop="retryItem(index)">
                    {{ $t("retry") }}
                  </v-btn>
                </div>

                <v-fade-transition v-if="!item.uploading && deletable && !disabled">
                  <div
                    v-if="hover"
                    class="file-upload-remove d-flex align-center justify-center"
                    @click.stop="removeItem(index)"
                  >
                    <v-btn fab dark x-small color="error">
                      <v-icon small>mdi-delete</v-icon>
                    </v-btn>
                  </div>
                </v-fade-transition>
              </v-img>

              <div
                v-else
                class="file-upload-file-tile d-flex flex-column align-center justify-center pa-2"
              >
                <v-icon size="32" color="grey darken-1">{{ icon }}</v-icon>
                <span class="caption text-truncate mt-1" style="max-width: 100%">{{
                  item.name
                }}</span>

                <div
                  v-if="item.uploading"
                  class="file-upload-overlay d-flex flex-column align-center justify-center"
                >
                  <v-progress-circular
                    :value="item.progress"
                    size="30"
                    width="3"
                    color="primary"
                  />
                  <span class="caption white--text mt-1">{{ item.progress }}%</span>
                </div>

                <div
                  v-if="item.error"
                  class="file-upload-overlay file-upload-overlay--error d-flex flex-column align-center justify-center"
                >
                  <v-icon color="white">mdi-alert-circle-outline</v-icon>
                  <span class="caption white--text mt-1">{{ $t("upload_failed") }}</span>
                  <v-btn x-small text color="white" @click.stop="retryItem(index)">
                    {{ $t("retry") }}
                  </v-btn>
                </div>

                <v-fade-transition v-if="!item.uploading && deletable && !disabled">
                  <div
                    v-if="hover"
                    class="file-upload-remove d-flex align-center justify-center"
                    @click.stop="removeItem(index)"
                  >
                    <v-btn fab dark x-small color="error">
                      <v-icon small>mdi-delete</v-icon>
                    </v-btn>
                  </div>
                </v-fade-transition>
              </div>
            </v-card>
          </v-hover>
        </v-col>
      </v-row>
    </template>

    <div v-if="errorMessage" class="error--text caption pl-1 mt-1">
      {{ errorMessage }}
    </div>
  </div>
</template>

<script>
import apiService from "@/services/api";

let uidCounter = 0;

const TYPE_PRESETS = {
  image: { accept: "image/*", icon: "mdi-image-outline" },
  document: { accept: ".pdf,.doc,.docx,.xls,.xlsx", icon: "mdi-file-document-outline" },
  file: { accept: "*/*", icon: "mdi-file-outline" },
};

export default {
  name: "FileUpload",

  props: {
    // v-model: single object ({ id, url, name, ... }) when multiple=false, or an Array of such objects when multiple=true
    value: {
      type: [Array, Object],
      default: null,
    },
    multiple: {
      type: Boolean,
      default: false,
    },
    // Preset controlling default accept + icon: "image" | "document" | "file"
    type: {
      type: String,
      default: "file",
    },
    // Overrides the native <input accept> derived from `type`
    accept: {
      type: String,
      default: null,
    },
    // "dropzone" (drag & drop card, default) or "avatar" (compact circular picker)
    variant: {
      type: String,
      default: "dropzone",
    },
    label: {
      type: String,
      default: "",
    },
    hint: {
      type: String,
      default: "",
    },
    required: {
      type: Boolean,
      default: false,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    compact: {
      type: Boolean,
      default: false,
    },
    avatarSize: {
      type: [Number, String],
      default: 72,
    },
    previewCols: {
      type: [Number, String],
      default: 3,
    },
    maxFiles: {
      type: Number,
      default: 5,
    },
    maxSizeMb: {
      type: Number,
      default: 5,
    },
    deletable: {
      type: Boolean,
      default: true,
    },
    // Endpoint used to auto-upload each selected file, e.g. "/gallery"
    uploadUrl: {
      type: String,
      default: null,
    },
    // Endpoint used to delete an already uploaded item: `${deleteUrl}/${id}`
    deleteUrl: {
      type: String,
      default: null,
    },
    // FormData field name the backend expects for the file
    fieldName: {
      type: String,
      default: "file",
    },
    // Extra fields appended to the upload FormData (e.g. { supplier_id: 1 })
    extraData: {
      type: Object,
      default: () => ({}),
    },
    // Automatically upload to `uploadUrl` as soon as a file is picked.
    // Set to false to only collect files locally (e.g. entity not yet saved) -
    // the raw File is exposed on each item as `item.file` for the parent to upload later.
    autoUpload: {
      type: Boolean,
      default: true,
    },
    // Optional function(responseData) => { id, url } to customize how the upload response maps to the preview item
    responseMap: {
      type: Function,
      default: null,
    },
  },

  data() {
    return {
      dragging: false,
      errorMessage: "",
    };
  },

  computed: {
    items() {
      if (!this.value) return [];
      return this.multiple ? this.value : [this.value];
    },
    computedAccept() {
      return this.accept || TYPE_PRESETS[this.type]?.accept || "*/*";
    },
    icon() {
      return TYPE_PRESETS[this.type]?.icon || "mdi-cloud-upload-outline";
    },
    atMaxFiles() {
      return this.multiple && this.items.length >= this.maxFiles;
    },
    hasError() {
      return this.items.some((i) => i.error);
    },
    isImage() {
      return this.type === "image";
    },
    dropText() {
      return this.$t("drag_drop_here");
    },
    hintText() {
      if (this.hint) return this.hint;
      const parts = [];
      if (this.type === "image") parts.push("JPG, PNG, WEBP");
      parts.push(this.$t("max_size_hint", { size: this.maxSizeMb }));
      if (this.multiple) parts.push(this.$t("max_files_hint", { count: this.maxFiles }));
      return parts.join(" • ");
    },
  },

  methods: {
    isImageItem(item) {
      return (
        this.isImage ||
        /^image\//.test(item?.file?.type || "") ||
        (item?.url || "").startsWith("data:image")
      );
    },
    openPicker() {
      if (this.disabled || this.atMaxFiles) return;
      this.$refs.input.click();
    },
    onDragOver() {
      if (this.disabled || this.atMaxFiles) return;
      this.dragging = true;
    },
    onDrop(e) {
      this.dragging = false;
      if (this.disabled || this.atMaxFiles) return;
      this.handleFiles(Array.from(e.dataTransfer?.files || []));
    },
    onFilesSelected(e) {
      this.handleFiles(Array.from(e.target.files || []));
      this.$refs.input.value = "";
    },
    handleFiles(files) {
      if (!files.length) return;
      this.errorMessage = "";

      let candidates = files;
      if (!this.multiple) {
        candidates = files.slice(0, 1);
      } else {
        const remaining = this.maxFiles - this.items.length;
        if (remaining <= 0) {
          this.errorMessage = this.$t("max_files_reached", { max: this.maxFiles });
          return;
        }
        candidates = files.slice(0, remaining);
      }

      candidates.forEach((file) => this.addFile(file));
    },
    addFile(file) {
      const maxBytes = this.maxSizeMb * 1024 * 1024;
      if (file.size > maxBytes) {
        this.errorMessage = this.$t("file_too_large", { size: this.maxSizeMb });
        return;
      }

      const item = {
        uid: `up-${++uidCounter}`,
        id: null,
        file,
        name: file.name,
        url: null,
        uploading: !!(this.autoUpload && this.uploadUrl),
        progress: 0,
        error: false,
      };

      if (/^image\//.test(file.type)) {
        const reader = new FileReader();
        reader.onload = (e) => {
          item.url = e.target.result;
          this.commitItem(item);
          if (this.autoUpload && this.uploadUrl) this.upload(item);
        };
        reader.readAsDataURL(file);
      } else {
        this.commitItem(item);
        if (this.autoUpload && this.uploadUrl) this.upload(item);
      }
    },
    commitItem(item) {
      if (this.multiple) {
        this.$emit("input", [...this.items, item]);
      } else {
        this.$emit("input", item);
      }
      this.$emit("change", item);
    },
    updateItem(uidOrIndex, patch, byIndex = false) {
      const items = this.items.map((it, idx) => {
        const match = byIndex ? idx === uidOrIndex : it.uid === uidOrIndex;
        return match ? { ...it, ...patch } : it;
      });
      this.$emit("input", this.multiple ? items : items[0] ?? null);
    },
    upload(item) {
      const formData = new FormData();
      formData.append(this.fieldName, item.file);
      Object.entries(this.extraData || {}).forEach(([key, val]) => {
        if (val !== undefined && val !== null) formData.append(key, val);
      });

      apiService
        .post(this.uploadUrl, formData, {
          headers: { "Content-Type": "multipart/form-data" },
          onUploadProgress: (evt) => {
            const progress = evt.total ? Math.round((evt.loaded * 100) / evt.total) : 0;
            this.updateItem(item.uid, { progress });
          },
        })
        .then((response) => {
          const mapped = this.responseMap
            ? this.responseMap(response.data)
            : { id: response.data?.id ?? null, url: response.data?.url || null };
          this.updateItem(item.uid, {
            uploading: false,
            progress: 100,
            error: false,
            id: mapped.id,
            url: mapped.url || item.url,
            response: response.data,
          });
          this.$emit("upload-success", { item, response: response.data });
        })
        .catch((error) => {
          this.updateItem(item.uid, { uploading: false, error: true });
          this.$emit("upload-error", { item, error });
        });
    },
    retryItem(index) {
      const item = this.items[index];
      if (!item || !item.file) return;
      this.updateItem(index, { uploading: true, error: false, progress: 0 }, true);
      this.upload(item);
    },
    removeItem(index) {
      const item = this.items[index];
      const remaining = this.items.filter((_, i) => i !== index);

      if (item?.id && this.deleteUrl) {
        apiService.delete(`${this.deleteUrl}/${item.id}`).catch(() => {});
      }

      this.$emit("input", this.multiple ? remaining : null);
      this.$emit("remove", item);
    },
  },
};
</script>

<style scoped>
.file-upload-zone {
  width: 100%;
  cursor: pointer;
  border-style: dashed !important;
  border-width: 2px !important;
  border-color: rgba(0, 0, 0, 0.18) !important;
  background: rgba(0, 0, 0, 0.015);
  transition: border-color 0.15s ease, background-color 0.15s ease;
}
.file-upload-zone:hover {
  border-color: var(--v-primary-base, #1976d2) !important;
  background: rgba(25, 118, 210, 0.03);
}
.file-upload-zone--dragging {
  border-color: var(--v-primary-base, #1976d2) !important;
  background: rgba(25, 118, 210, 0.06);
}
.file-upload-zone--error {
  border-color: var(--v-error-base, #ff5252) !important;
}
.file-upload-zone--disabled {
  cursor: not-allowed;
  opacity: 0.6;
  pointer-events: none;
}

.file-upload-preview {
  position: relative;
  overflow: hidden;
}
.file-upload-preview--error {
  border-color: var(--v-error-base, #ff5252) !important;
}

.file-upload-file-tile {
  height: 100%;
  min-height: 96px;
  background: rgba(0, 0, 0, 0.02);
}

.file-upload-overlay {
  position: absolute;
  inset: 0;
  background: rgba(0, 0, 0, 0.45);
}
.file-upload-overlay--error {
  background: rgba(255, 82, 82, 0.55);
}

.file-upload-remove {
  position: absolute;
  inset: 0;
  background: rgba(0, 0, 0, 0.4);
  cursor: pointer;
}

.file-upload-badge {
  position: absolute;
  top: 4px;
  left: 4px;
}

.file-upload-avatar {
  cursor: pointer;
  border: 2px dashed rgba(0, 0, 0, 0.18);
  position: relative;
  transition: border-color 0.15s ease;
}
.file-upload-avatar:hover {
  border-color: var(--v-primary-base, #1976d2);
}
.file-upload-avatar--empty {
  background: rgba(0, 0, 0, 0.03);
}
.file-upload-avatar--disabled {
  cursor: not-allowed;
  opacity: 0.6;
  pointer-events: none;
}
.file-upload-avatar-edit {
  position: absolute;
  bottom: -2px;
  right: -2px;
  width: 22px;
  height: 22px;
  border-radius: 50%;
  background: var(--v-primary-base, #1976d2);
  display: flex;
  align-items: center;
  justify-content: center;
  border: 2px solid #fff;
}
</style>
